import type { RfqExcelFormData, RfqExcelItem } from './rfq-excel.types';
import { formatContactPhone } from './contact-phone';

export type RfqSubmitAction = 'draft' | 'sent';

export interface BuildAdminRfqPayloadInput {
  formData: Partial<RfqExcelFormData> & Record<string, unknown>;
  items: RfqExcelItem[];
  action: RfqSubmitAction;
  rfqNumber?: string | null;
  pmsJobId?: string | null;
  /** Optional vessel list to resolve display name when form stores vessel _id. */
  vessels?: Array<{ _id: string; name: string; imoNumber?: string }>;
}

function isLikelyObjectId(value: string): boolean {
  return /^[a-fA-F0-9]{24}$/.test(String(value ?? '').trim());
}

function nonEmpty(value: unknown): string | undefined {
  if (value == null) return undefined;
  const text = String(value).trim();
  return text || undefined;
}

/** Map Create RFQ form + items into the admin create/update request body (JSON shape). */
export function buildAdminRfqPersistencePayload(
  input: BuildAdminRfqPayloadInput
): Record<string, unknown> {
  const { formData, items, action, rfqNumber, pmsJobId, vessels = [] } = input;
  const vendorIds = Array.isArray(formData.selectedVendorIds)
    ? formData.selectedVendorIds
    : [];

  const externalVendorInvites = Array.isArray(formData.externalVendorInvites)
    ? (formData.externalVendorInvites as Array<{ email?: string; companyName?: string }>)
        .map((row) => ({
          email: String(row?.email || '').trim().toLowerCase(),
          companyName: String(row?.companyName || '').trim() || undefined,
        }))
        .filter((row) => row.email.includes('@'))
    : [];

  const referenceName =
    (typeof formData.category === 'string' && formData.category) ||
    (typeof formData.brand === 'string' && formData.brand) ||
    'Equipment';
  const categoryInfo = formData.category ? ` - ${formData.category}` : '';
  const brandInfo = formData.brand ? ` (${formData.brand})` : '';
  const title = `RFQ for ${referenceName}${categoryInfo}${brandInfo}`;
  const status = action === 'draft' ? 'draft' : 'sent';

  const rfqItems = items
    .filter((item) => {
      if (action === 'sent') return true;
      return Boolean(
        item.itemDescription?.trim() ||
          item.partNo?.trim() ||
          item.impaNo?.trim() ||
          item.requiredQuantity
      );
    })
    .map((item) => ({
      impaNo: item.impaNo,
      itemDescription: item.itemDescription || (action === 'draft' ? 'Draft line item' : ''),
      drawingNumber: item.drawingNumber,
      partNo: item.partNo,
      altPartNo: item.altPartNo,
      positionNo: item.positionNo,
      modelSerial: item.modelSerial || undefined,
      dimensions: item.dimensions,
      requiredQuantity: item.requiredQuantity
        ? parseInt(item.requiredQuantity.toString(), 10) || 0
        : 0,
      uom: item.uom || (action === 'draft' ? 'Pcs' : item.uom),
      quality: item.quality || undefined,
      generalRemark: item.generalRemark || '-',
      deliveryPriority: item.deliveryPriority || 'normal',
      expectedDeliveryDate: item.expectedDeliveryDate || undefined,
    }));

  const vesselRaw = nonEmpty(formData.vesselName) ?? '';
  const vesselMatch = vessels.find((v) => v._id === vesselRaw || v.name === vesselRaw);
  const vesselId =
    (vesselMatch?._id && isLikelyObjectId(vesselMatch._id) ? vesselMatch._id : undefined) ||
    (isLikelyObjectId(vesselRaw) ? vesselRaw : undefined);
  const vesselDisplayName =
    vesselMatch?.name ||
    (!isLikelyObjectId(vesselRaw) ? vesselRaw : undefined) ||
    undefined;
  const imoNumber =
    nonEmpty(formData.imoNo) || nonEmpty(vesselMatch?.imoNumber) || undefined;

  const deliveryAtPort = formData.deliveryRequiredAt === 'port';
  const deliveryAtWarehouse = formData.deliveryRequiredAt === 'warehouse';

  const metadata: Record<string, unknown> = {
    quoteDeadline: formData.quoteDeadline,
    quoteValidityDays: formData.quoteValidityDays,
    urgencyLevel: formData.urgencyLevel,
    requisitionRef: formData.requisitionRef,
    currency: formData.currency,
    revisionNo: formData.revisionNo,
    estimatedTotalCost: formData.estimatedTotalCost,
    deliveryRequiredAt: formData.deliveryRequiredAt,
    deliveryType: formData.deliveryType,
    portId: deliveryAtPort ? nonEmpty(formData.portId) : undefined,
    portName: deliveryAtPort ? formData.portName : undefined,
    portCountry: deliveryAtPort ? formData.portCountry : undefined,
    portAgentId: deliveryAtPort ? nonEmpty(formData.portAgentId) : undefined,
    portAgentName: deliveryAtPort ? formData.portAgentName : undefined,
    portAddress: deliveryAtPort ? formData.portAddress : undefined,
    portEtaDate: deliveryAtPort ? formData.portEtaDate : undefined,
    warehouseAddress1: deliveryAtWarehouse ? formData.warehouseAddress1 : undefined,
    warehousePincode: deliveryAtWarehouse ? formData.warehousePincode : undefined,
    warehouseCity: deliveryAtWarehouse ? formData.warehouseCity : undefined,
    warehouseState: deliveryAtWarehouse ? formData.warehouseState : undefined,
    warehouseCountry: deliveryAtWarehouse ? formData.warehouseCountry : undefined,
    creditType: formData.creditType || undefined,
    paymentTerm: formData.paymentTerm || undefined,
    insuranceTerm: formData.insuranceTerm || undefined,
    taxTerm: formData.taxTerm || undefined,
    transportTerm: formData.transportTerm || undefined,
    deliveryTerm: formData.deliveryTerm || undefined,
    packingTerm: formData.packingTerm || undefined,
    contactName: formData.contactName,
    contactEmail: formData.contactEmail,
    contactPhone: formatContactPhone(
      String(formData.contactPhoneCountryCode ?? ''),
      String(formData.contactPhone ?? '')
    ),
    contactPhoneCountryCode: formData.contactPhoneCountryCode,
    contactDepartment: nonEmpty(formData.contactDepartment),
    requiredPaymentTerms: formData.requiredPaymentTerms,
    leadDate: nonEmpty(formData.leadDate),
    requiredCertificates: Array.isArray(formData.requiredCertificates)
      ? formData.requiredCertificates
      : undefined,
    requiredDocuments: Array.isArray(formData.requiredDocuments)
      ? formData.requiredDocuments
      : undefined,
    packingInstructions: nonEmpty(formData.packingInstructions),
    partialDeliveryAllowed: nonEmpty(formData.partialDeliveryAllowed),
    partialQuoteAllowed: nonEmpty(formData.partialQuoteAllowed),
    splitOrderAllowed: nonEmpty(formData.splitOrderAllowed),
    // Mirror vessel identifiers in metadata for readers that prefer Mixed
    vesselName: vesselDisplayName || vesselRaw || undefined,
    imoNumber,
    ...(pmsJobId ? { pmsJobId, source: 'pms_job_card' } : {}),
  };

  const payload: Record<string, unknown> = {
    title,
    rfqNumber: rfqNumber || undefined,
    description: formData.remarks || `RFQ for ${referenceName}`,
    equipmentTags: formData.equipmentTags,
    category: formData.category,
    subCategory: formData.subCategory,
    department: formData.department,
    brand: formData.brand,
    model: formData.model,
    remarks: formData.remarks,
    leadDate: nonEmpty(formData.leadDate),
    vesselId: vesselId || undefined,
    vesselName: vesselDisplayName || vesselRaw || undefined,
    imoNumber,
    recipientVendorIds: vendorIds,
    externalVendorInvites:
      externalVendorInvites.length > 0 ? externalVendorInvites : undefined,
    items: rfqItems,
    status,
    metadata,
  };

  return payload;
}

/** Merge typed rfq.items with richer metadata.items for draft hydrate. */
export function mergeRfqItemsForHydrate(
  rfqItems: unknown,
  metaItems: unknown
): Record<string, unknown>[] {
  const typed = Array.isArray(rfqItems) ? (rfqItems as Record<string, unknown>[]) : [];
  const mixed = Array.isArray(metaItems) ? (metaItems as Record<string, unknown>[]) : [];
  const primary = typed.length > 0 ? typed : mixed;
  const secondary = mixed.length > 0 ? mixed : typed;

  return primary.map((item, index) => {
    const other =
      secondary.find(
        (s) =>
          (s.partNo && item.partNo && String(s.partNo) === String(item.partNo)) ||
          (s.impaNo && item.impaNo && String(s.impaNo) === String(item.impaNo))
      ) || secondary[index] ||
      {};

    const pick = (key: string, fallback = '') =>
      String(item[key] ?? other[key] ?? fallback);

    return {
      id: String(item.id || other.id || index + 1),
      impaNo: pick('impaNo'),
      itemDescription: pick('itemDescription'),
      drawingNumber: pick('drawingNumber'),
      partNo: pick('partNo'),
      altPartNo: pick('altPartNo'),
      modelSerial: pick('modelSerial'),
      positionNo: pick('positionNo'),
      dimensions: pick('dimensions'),
      requiredQuantity: pick('requiredQuantity'),
      uom: pick('uom'),
      quality: pick('quality'),
      generalRemark: pick('generalRemark'),
      deliveryPriority: pick('deliveryPriority', 'normal'),
      expectedDeliveryDate: pick('expectedDeliveryDate'),
    };
  });
}
